import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { layoutViewAtom } from 'src/shared/layoutView';
import { useIsMobile } from './useIsMobile';
import { useLocalStorage } from './useLocalStorage';

// Note about the value semantics (existing quirk, see
// `src/components/LayoutView/LayoutView.tsx`):
//
//   - `layoutView === 'list'` actually renders the *gallery* (card grid).
//   - `layoutView === 'grid'` actually renders the *row* layout.
//
// The names are inverted relative to the rendered output and we don't
// untangle that here — we just preserve existing behaviour. On phones
// the row layout's hover-only action overlay is unusable, so we force
// the gallery (cards) regardless of the persisted preference; the
// preference is left untouched in localStorage so it's restored on
// desktop.

const useLayoutSwitcher = () => {
  const [layoutView, setLayoutView] = useAtom(layoutViewAtom);
  const [savedLayoutView, saveLayoutView] = useLocalStorage('dashboard.layout', 'list');
  const isMobile = useIsMobile();

  const toggleLayoutView = () => {
    const layout = layoutView === 'list' ? 'grid' : 'list';
    setLayoutView(layout);
    saveLayoutView(layout);
  };

  useEffect(() => {
    setLayoutView(savedLayoutView as 'list' | 'grid');
  }, []);

  const effectiveLayoutView: 'list' | 'grid' = isMobile ? 'list' : layoutView;

  return {
    layoutView: effectiveLayoutView,
    toggleLayoutView,
    isLayoutGrid: effectiveLayoutView === 'grid',
    isLayoutList: effectiveLayoutView === 'list',
  };
};

export default useLayoutSwitcher;
